/**
 * api.js - Centralized API client for the PlayerIQ backend.
 *
 * Every network request in the app goes through this file. The `request`
 * function injects the JWT, handles 401 redirects, and throws on errors so
 * callers only need to deal with resolved data.
 */

const BASE_URL = process.env.REACT_APP_API_URL || '';

// --- Token helpers ---

// These three helpers keep token management in one place so no other
// file has to know the localStorage key names.
export const getToken = () => localStorage.getItem('access_token');
export const setToken = (token) => localStorage.setItem('access_token', token);
export const clearToken = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('user_role');
  localStorage.removeItem('user_first_name');
};

// --- Core fetch wrapper ---

/**
 * Wraps the native fetch API with auth headers and error handling.
 * All API methods below call this instead of fetch directly.
 *
 * @param {string} path - API path, e.g. '/api/auth/login'
 * @param {object} options - Standard fetch options (method, body, etc.)
 * @returns {Promise<any>} Parsed JSON response, or null for 204 No Content.
 */
async function request(path, options = {}) {
  const token = getToken();

  const headers = {
    'Content-Type': 'application/json',
    // Only attach the Authorization header if a token exists
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers,
  });

  // Token expired or invalid — clear local state and kick back to landing
  if (res.status === 401) {
    clearToken();
    window.location.href = '/';
    return;
  }

  // 204 No Content — nothing to parse
  if (res.status === 204) return null;

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    const message = data?.detail || `Request failed: ${res.status}`;
    throw new Error(message);
  }

  return data;
}

// --- API methods ---

export const api = {

  // --- Auth ---

  login: ({ email, password }) =>
    request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  register: ({ email, password, first_name, last_name }) =>
    request('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, first_name, last_name }),
    }),

  demoLogin: () => request('/api/auth/demo-login', { method: 'POST' }),

  logout: () => {
    clearToken();
  },

  forgotPassword: ({ email }) =>
    request('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  resetPassword: (token, new_password) =>
    request('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, new_password }),
    }),

  // --- User profile ---

  getMe: () => request('/api/users/me'),

  updateMe: ({ first_name, last_name, email }) =>
    request('/api/users/me', {
      method: 'PATCH',
      body: JSON.stringify({ first_name, last_name, email }),
    }),

  changePassword: ({ current_password, new_password }) =>
    request('/api/users/me/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password, new_password }),
    }),

  // --- Favorites ---

  getFavorites: () => request('/api/users/me/favorites'),

  addFavorite: (player_id) =>
    request('/api/users/me/favorites', {
      method: 'POST',
      body: JSON.stringify({ player_id }),
    }),

  removeFavorite: (player_id) =>
    request(`/api/users/me/favorites/${player_id}`, {
      method: 'DELETE',
    }),

  // --- Projections ---

  getProjections: ({ season = 2024, week = 18, position = null } = {}) => {
    const params = new URLSearchParams({ season, week });
    if (position && position !== 'All') params.append('position', position);
    return request(`/api/predictions/players?${params}`);
  },

  getAvailableWeeks: () => request('/api/projections/available-weeks'),

  // --- Stats / Leaderboard ---

  getSeasonAvailability: () => request('/api/stats/seasons'),

  getLeaderboard: ({ season = 2024, position = null, name = null, limit = 50, page = 1 } = {}) => {
    const params = new URLSearchParams({ season, limit, page });
    if (position) params.append('position', position);
    if (name) params.append('name', name);
    return request(`/api/stats/leaderboard?${params}`);
  },

  getPlayerHistory: (playerId, seasons = '2023,2024') =>
    request(`/api/stats/player/${playerId}?seasons=${seasons}`),

  searchPlayers: (q) => request(`/api/stats/search?q=${encodeURIComponent(q)}`),

  // --- Analytics / Model metrics ---

  getMetricsAvailability: () => request('/api/analytics/availability'),

  getMetrics: (season = 2024) =>
    request(`/api/analytics/metrics?season=${season}`),

  // --- Season accuracy (aggregated across all weeks) ---

  getSeasonAccuracy: (season) => request(`/api/predictions/accuracy?season=${season}`),

  // --- Live player status (Sleeper API) ---

  getPlayerStatuses: () => request('/api/players/status'),

  // --- Fantasy teams ---

  getFantasyTeams: () => request('/api/fantasy-teams'),

  saveFantasyTeam: ({ name, season, week, roster }) =>
    request('/api/fantasy-teams', {
      method: 'POST',
      body: JSON.stringify({ name, season, week, roster }),
    }),

  updateFantasyTeam: (id, { name, roster }) =>
    request(`/api/fantasy-teams/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ name, roster }),
    }),

  deleteFantasyTeam: (id) =>
    request(`/api/fantasy-teams/${id}`, { method: 'DELETE' }),

  // --- Health check ---

  health: () => request('/health'),
};
